import { create } from 'zustand';
import { login, resendOtp, submitOtp, getUserInfo } from '@libs/api/auth';
import { getFreshToken } from '@libs/api/auth';
import { updateToken } from '@libs/api/request';
import { parseError } from '@libs/api/errors';
import { SystemUser } from '@libs/models/common';
import { parseJwt } from '@libs/utils/jwt';
import { Analytics } from '@libs/analytics';
import { clearUserLocalData } from '@libs/localStorage';
import { Userpilot } from 'userpilot';

type State = {
  loading: boolean;
  userData: SystemUser | null;
  userDataLoading: boolean;
  token: string | null;
  refreshToken: string | null;
  refreshTimer: null | NodeJS.Timeout;

  fetchUserData: () => Promise<void>;
  login: (email: string, tenantID?: string) => Promise<void>;
  flushLogin: () => void;
  setToken: (token: string, refreshToken: string) => void;
  submitOtp: (email: string, otp: string) => Promise<void>;
  resendOtp: (email: string) => Promise<void>;
  isAuthenticated: () => boolean;
  initRefreshTokenProcedure: () => void;
};

export const useAuthStore = create<State>()((set, get) => ({
  loading: false,
  userDataLoading: false,
  userData: null,
  token: localStorage.getItem('token'),
  refreshToken: localStorage.getItem('refreshToken'),
  refreshTimer: null,

  fetchUserData: async () => {
    set({
      userDataLoading: true,
    });
    const profileInfo = await getUserInfo();
    // Update Amplitude user properties
    Analytics.setUserId(profileInfo.user.id);
    Userpilot.identify(profileInfo.user.id, {
      email: profileInfo.user.email,
    });
    set({
      userData: profileInfo.user,
      userDataLoading: false,
    });
  },
  login: async (email, tenantId) => {
    set({
      loading: true,
    });
    try {
      await login(email, tenantId);
    } finally {
      set({
        loading: false,
      });
    }
  },

  flushLogin: () => {
    const { refreshTimer } = get();
    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }
    clearUserLocalData();
    updateToken(null);
    set({ token: null, refreshToken: null });
    window.location.reload();
  },

  setToken: (token, refreshToken) => {
    const { initRefreshTokenProcedure } = get();
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    updateToken(token);
    set({ token, refreshToken });
    initRefreshTokenProcedure();
  },

  submitOtp: async (email, otp) => {
    set({
      loading: true,
    });
    const { setToken } = get();
    try {
      const res = await submitOtp(email, otp);
      const { token, refreshToken } = res.data;
      setToken(token, refreshToken);
    } catch (error) {
      throw parseError(error);
    } finally {
      set({
        loading: false,
      });
    }
  },

  resendOtp: async (email) => {
    set({
      loading: true,
    });
    try {
      await resendOtp(email);
    } finally {
      set({
        loading: false,
      });
    }
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  initRefreshTokenProcedure: () => {
    checkForRefresh();
    async function checkForRefresh() {
      const { token, setToken, refreshTimer, refreshToken } = get();
      if (!token || !refreshToken) {
        return;
      }
      const data = parseJwt<{ exp: number }>(token);
      const now = Date.now() / 1000;
      // 5 min before token expires
      const refreshTime = data.exp - 60 * 5;

      if (now >= refreshTime) {
        const response = await getFreshToken(refreshToken);
        setToken(response.data.token, response.data.refreshToken);
        checkForRefresh();
        return;
      }

      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
      set({
        // 1 ms after refreshTime
        refreshTimer: setTimeout(checkForRefresh, (refreshTime - now) * 1000 + 1),
      });
    }
  },
}));
